import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  Paper,
  Grid,
  Snackbar,
  Alert,
  CardMedia,
  TextField,
  Autocomplete,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import {
  Sync as SyncIcon,
  Info as InfoIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import axios from "axios";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../../../Firebase/Firebase";
import { YOUR_YOUTUBE_API_KEY } from "../../../Assets/Secretkeys/Secretkey";

const UpdateLink = () => {
  const [allVideos, setAllVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [filters, setFilters] = useState({
    title: null,
    uploadDate: null,
    channelTitle: null,
  });
  const [filterOptions, setFilterOptions] = useState({
    titles: [],
    dates: [],
    channels: [],
  });
  const [loading, setLoading] = useState({ page: true, video: null });
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    video: null,
  });

  const API_KEY = YOUR_YOUTUBE_API_KEY;

  useEffect(() => {
    fetchAllVideosFromDB();
  }, []);

  const fetchAllVideosFromDB = async () => {
    setLoading((prev) => ({ ...prev, page: true }));
    try {
      const querySnapshot = await getDocs(collection(db, "linkCollections"));
      const videosData = [];

      querySnapshot.forEach((doc) => {
        const collectionData = doc.data();
        const currentCollectionName = collectionData.collectionName || doc.id;

        const collectionVideos = collectionData.videos || [];
        collectionVideos.forEach((video) => {
          videosData.push({
            ...video,
            collectionId: doc.id,
            collectionName: currentCollectionName,
            firebaseDocId: doc.id, // Store document ID for deletion
          });
        });
      });

      const uniqueTitles = [...new Set(videosData.map((v) => v.title))];
      const uniqueDates = [
        ...new Set(videosData.map((v) => v.uploadDate).filter(Boolean)),
      ].sort((a, b) => new Date(b) - new Date(a));
      const uniqueChannels = [
        ...new Set(videosData.map((v) => v.channelTitle).filter(Boolean)),
      ];

      setAllVideos(videosData);
      setFilteredVideos(videosData);
      setFilterOptions({
        titles: uniqueTitles,
        dates: uniqueDates,
        channels: uniqueChannels,
      });
    } catch (error) {
      console.error("Error fetching videos: ", error);
      setNotification({
        open: true,
        message: "Failed to load videos from the database.",
        severity: "error",
      });
    } finally {
      setLoading((prev) => ({ ...prev, page: false }));
    }
  };

  // Filtering logic
  useEffect(() => {
    let tempVideos = [...allVideos];

    if (filters.title) {
      tempVideos = tempVideos.filter((video) => video.title === filters.title);
    }

    if (filters.uploadDate) {
      tempVideos = tempVideos.filter(
        (video) => video.uploadDate === filters.uploadDate
      );
    }

    if (filters.channelTitle) {
      tempVideos = tempVideos.filter(
        (video) => video.channelTitle === filters.channelTitle
      );
    }

    setFilteredVideos(tempVideos);
  }, [filters, allVideos]);

  const parseDuration = (isoDuration) => {
    const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
    const matches = isoDuration.match(regex);
    const hours = parseInt(matches[1] || 0);
    const minutes = parseInt(matches[2] || 0);
    const seconds = parseInt(matches[3] || 0);
    return `${hours > 0 ? String(hours).padStart(2, "0") + ":" : ""}${String(
      minutes
    ).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  const handleUpdateVideo = async (videoToUpdate) => {
    setLoading((prev) => ({ ...prev, video: videoToUpdate.videoId }));

    try {
      const response = await axios.get(
        `https://www.googleapis.com/youtube/v3/videos?id=${videoToUpdate.videoId}&part=snippet,contentDetails,statistics&key=${API_KEY}`
      );

      const videoData = response.data.items[0];
      if (!videoData) {
        throw new Error("Video not found on YouTube.");
      }

      const updatedVideoData = {
        id: videoData.id,
        title: videoData.snippet.title,
        description: videoData.snippet.description,
        channelTitle: videoData.snippet.channelTitle,
        videoId: videoData.id,
        views: Number(videoData.statistics.viewCount).toLocaleString(),
        duration: parseDuration(videoData.contentDetails.duration),
        uploadDate: new Date(videoData.snippet.publishedAt)
          .toISOString()
          .split("T")[0],
        thumbnail: videoData.snippet.thumbnails.medium.url,
      };

      const docRef = doc(db, "linkCollections", videoToUpdate.collectionId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const collectionData = docSnap.data();
        const newVideosArray = collectionData.videos.map((v) =>
          v.videoId === updatedVideoData.videoId ? updatedVideoData : v
        );
        await updateDoc(docRef, { videos: newVideosArray });

        setAllVideos((prevAllVideos) =>
          prevAllVideos.map((v) =>
            v.videoId === updatedVideoData.videoId
              ? {
                  ...updatedVideoData,
                  collectionId: v.collectionId,
                  collectionName: v.collectionName,
                  firebaseDocId: v.firebaseDocId,
                }
              : v
          )
        );
      }

      setNotification({
        open: true,
        message: `Successfully updated '${updatedVideoData.title}'!`,
        severity: "success",
      });
    } catch (err) {
      console.error(err);
      const errorMessage =
        err.response?.data?.error?.message || "Failed to update video details.";
      setNotification({ open: true, message: errorMessage, severity: "error" });
    } finally {
      setLoading((prev) => ({ ...prev, video: null }));
    }
  };

  // Delete video function
  const handleDeleteVideo = async (videoToDelete) => {
    setLoading((prev) => ({ ...prev, video: videoToDelete.videoId }));

    try {
      const docRef = doc(db, "linkCollections", videoToDelete.collectionId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const collectionData = docSnap.data();
        const updatedVideosArray = collectionData.videos.filter(
          (v) => v.videoId !== videoToDelete.videoId
        );

        if (updatedVideosArray.length === 0) {
          // If no videos left in collection, delete the entire collection document
          await deleteDoc(docRef);
        } else {
          // Update the collection with remaining videos
          await updateDoc(docRef, { videos: updatedVideosArray });
        }

        // Update local state
        setAllVideos((prevAllVideos) =>
          prevAllVideos.filter((v) => v.videoId !== videoToDelete.videoId)
        );

        setNotification({
          open: true,
          message: `Successfully deleted '${videoToDelete.title}'!`,
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error deleting video: ", error);
      setNotification({
        open: true,
        message: "Failed to delete video from the database.",
        severity: "error",
      });
    } finally {
      setLoading((prev) => ({ ...prev, video: null }));
      setDeleteDialog({ open: false, video: null });
    }
  };

  // Open delete confirmation dialog
  const openDeleteDialog = (video) => {
    setDeleteDialog({ open: true, video });
  };

  // Close delete confirmation dialog
  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, video: null });
  };

  const clearFilters = () => {
    setFilters({ title: null, uploadDate: null, channelTitle: null });
  };

  const handleCloseNotification = (event, reason) => {
    if (reason === "clickaway") return;
    setNotification({ ...notification, open: false });
  };

  return (
    <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <FilterListIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
          <Typography variant="h4" component="h1">
            Filter & Update Videos
          </Typography>
        </Box>
        <Tooltip title="Refresh all data">
          <IconButton
            color="primary"
            onClick={fetchAllVideosFromDB}
            disabled={loading.page}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {loading.page ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper
          variant="outlined"
          sx={{ p: 3, borderRadius: 2, mb: 3, backgroundColor: "grey.50" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="h6" component="p">
              Filter Options
            </Typography>
            <Chip
              label={`${filteredVideos.length} videos`}
              color="primary"
              variant="outlined"
            />
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <Autocomplete
                size="small"
                options={filterOptions.titles}
                value={filters.title}
                onChange={(event, newValue) => {
                  setFilters((prev) => ({ ...prev, title: newValue }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Title"
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Autocomplete
                size="small"
                options={filterOptions.channels}
                value={filters.channelTitle}
                onChange={(event, newValue) => {
                  setFilters((prev) => ({ ...prev, channelTitle: newValue }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Channel"
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Autocomplete
                size="small"
                options={filterOptions.dates}
                value={filters.uploadDate}
                onChange={(event, newValue) => {
                  setFilters((prev) => ({ ...prev, uploadDate: newValue }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Upload Date"
                    variant="outlined"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                color="secondary"
                onClick={clearFilters}
                startIcon={<ClearIcon />}
                sx={{ height: "40px" }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {filteredVideos.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Showing {filteredVideos.length} of {allVideos.length} total videos
          </Typography>
          <Grid container spacing={3}>
            {filteredVideos.map((video) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={video.videoId}>
                <Card
                  sx={{
                    height: 450, // Slightly increased height for delete button
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 4,
                    },
                    position: "relative",
                  }}
                >
                  {/* Delete Button */}
                  <Tooltip title="Delete video">
                    <IconButton
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "rgba(255,255,255,0.9)",
                        "&:hover": {
                          backgroundColor: "error.light",
                          color: "white",
                        },
                        zIndex: 1,
                      }}
                      size="small"
                      onClick={() => openDeleteDialog(video)}
                      disabled={loading.video === video.videoId}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <CardMedia
                    component="img"
                    height="160"
                    image={video.thumbnail}
                    alt={video.title}
                    sx={{ objectFit: "cover" }}
                  />
                  <CardContent sx={{ flexGrow: 1, p: 2 }}>
                    <Typography
                      gutterBottom
                      variant="subtitle1"
                      component="div"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        height: "60px",
                        lineHeight: "1.2",
                        fontWeight: 600,
                        pr: 3, // Space for delete button
                      }}
                    >
                      {video.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {video.channelTitle || "N/A"}
                    </Typography>

                    <Box sx={{ mt: "auto", pt: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        👁️ {video.views} views
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        ⏱️ {video.duration}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        📅 {video.uploadDate}
                      </Typography>
                      <br />
                      <Chip
                        label={`Creation Date: ${video.collectionName}`}
                        size="small"
                        variant="outlined"
                        // sx={{ mb: 1 }}
                      />
                    </Box>
                  </CardContent>
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      size="small"
                      onClick={() => handleUpdateVideo(video)}
                      disabled={loading.video === video.videoId}
                      startIcon={
                        loading.video === video.videoId ? (
                          <CircularProgress size={16} />
                        ) : (
                          <SyncIcon fontSize="small" />
                        )
                      }
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      {loading.video === video.videoId
                        ? "Updating..."
                        : "Refresh"}
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {!loading.page && allVideos.length > 0 && filteredVideos.length === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No videos found matching your current filters. Please clear the
          filters to see all videos.
        </Alert>
      )}

      {!loading.page && allVideos.length === 0 && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2 }}>
          No videos found in the database. Please add some links first using the
          'Add Link' page.
        </Alert>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Video?</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the video "
            {deleteDialog.video?.title}"? This action cannot be undone and the
            video will be permanently removed from the collection.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteVideo(deleteDialog.video)}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            disabled={loading.video === deleteDialog.video?.videoId}
          >
            {loading.video === deleteDialog.video?.videoId
              ? "Deleting..."
              : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default UpdateLink;
